import { AppointmentPhoneIcon } from '@/components/atoms/icons';
import { openPhoneDialer, toDialablePhoneNumber } from '@/core/utils/phoneUtils';
import React, { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

export interface AppointmentSalesContactProps {
  /** Sales agent display name from profile. */
  readonly label: string;
  /** Sales agent phone — dial icon + tooltip only when present. */
  readonly phone: string;
  readonly color: string;
  /** listing = 14px; detail = 16px to match appointment meta body. */
  readonly size?: 'listing' | 'detail';
}

const ICON_SIZE = 14;
const TOOLTIP_GAP = 8;

function webHoverProps(
  canDial: boolean,
  setTooltipVisible: (visible: boolean) => void,
): { onHoverIn: () => void; onHoverOut: () => void } | null {
  if (Platform.OS !== 'web' || !canDial) {
    return null;
  }
  return {
    onHoverIn: () => setTooltipVisible(true),
    onHoverOut: () => setTooltipVisible(false),
  };
}

/**
 * Dial icon + sales agent name (brand color).
 * Tooltip is layout-anchored under the dial icon so it tracks resize/reflow.
 */
export function AppointmentSalesContact({
  label,
  phone,
  color,
  size = 'listing',
}: Readonly<AppointmentSalesContactProps>) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const trimmedPhone = phone.trim();
  const trimmedLabel = label.trim();
  const hasPhone = trimmedPhone.length > 0;
  // Profile fields only — no static label / mock phone substitution.
  const hasName = trimmedLabel.length > 0;
  const isDetail = size === 'detail';

  if (!hasName && !hasPhone) {
    return null;
  }

  const dialNumber = hasPhone
    ? toDialablePhoneNumber(trimmedPhone) || trimmedPhone
    : '';
  const canDial = dialNumber.length > 0;
  const showTooltip = tooltipVisible && canDial;
  const hoverProps = webHoverProps(canDial, setTooltipVisible);
  const rowStyle = isDetail ? styles.pressableDetail : styles.pressable;
  const nameStyle = isDetail ? styles.labelDetail : styles.label;

  const openDialer = (): void => {
    if (!canDial) {
      return;
    }
    openPhoneDialer(dialNumber);
  };

  const dialIcon = hasPhone ? (
    <View style={styles.iconSlot} collapsable={false}>
      <AppointmentPhoneIcon size={ICON_SIZE} color={color} />
      {showTooltip ? (
        <View
          style={styles.tooltip}
          pointerEvents="none"
          accessibilityElementsHidden
          testID="appointment-sales-phone-tooltip"
        >
          <View
            style={styles.tooltipArrow}
            testID="appointment-sales-phone-tooltip-arrow"
          />
          <View style={styles.tooltipBubble}>
            <Text style={styles.tooltipText}>{trimmedPhone}</Text>
          </View>
        </View>
      ) : null}
    </View>
  ) : null;

  const name = hasName ? (
    <Text style={[nameStyle, { color }]} numberOfLines={1}>
      {trimmedLabel}
    </Text>
  ) : null;

  const accessibilityLabel = [trimmedLabel, trimmedPhone]
    .filter((part) => part.length > 0)
    .join(', ');

  return (
    <View style={[styles.wrap, showTooltip ? styles.wrapRaised : null]}>
      {canDial ? (
        <Pressable
          accessibilityRole="link"
          accessibilityLabel={accessibilityLabel}
          onPress={openDialer}
          style={[rowStyle, styles.pressableDial]}
          {...hoverProps}
        >
          {dialIcon}
          {name}
        </Pressable>
      ) : (
        <View style={rowStyle} accessibilityRole="text">
          {dialIcon}
          {name}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
    flexShrink: 1,
    minWidth: 0,
    zIndex: 2,
    justifyContent: 'center',
    alignSelf: 'flex-start',
    overflow: 'visible',
  },
  wrapRaised: {
    zIndex: 100,
  },
  pressable: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
    minWidth: 0,
    height: 16,
    overflow: 'visible',
  },
  pressableDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
    height: 22,
    overflow: 'visible',
  },
  pressableDial: {
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as object) : null),
  },
  /** Anchor box for the tooltip — must stay relative so the tip tracks the icon. */
  iconSlot: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'visible',
    zIndex: 1,
  },
  label: {
    fontSize: 14,
    lineHeight: 16,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    flexShrink: 1,
    ...(Platform.OS === 'web' ? null : { includeFontPadding: false }),
  },
  labelDetail: {
    fontSize: 16,
    lineHeight: 22,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    flexShrink: 1,
    ...(Platform.OS === 'web' ? null : { includeFontPadding: false }),
  },
  tooltip: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: TOOLTIP_GAP,
    // Match icon width so the arrow stays centered on the dial glyph.
    width: ICON_SIZE,
    zIndex: 100,
    alignItems: 'center',
  },
  tooltipArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#1A1A1A',
  },
  tooltipBubble: {
    backgroundColor: '#1A1A1A',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  tooltipText: {
    color: '#FFFFFF',
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Matter-Regular',
  },
});

export default AppointmentSalesContact;
